
import React, { useState } from 'react';
import { AppConfig, Theme } from '../types';
import { supabaseService } from '../services/supabaseService';

interface AdminSettingsProps {
  config: AppConfig;
  onSave: (config: AppConfig) => void;
  theme: Theme;
}

const AdminSettings: React.FC<AdminSettingsProps> = ({ config, onSave, theme }) => {
  const [localConfig, setLocalConfig] = useState<AppConfig>({ ...config });
  const [newKeyword, setNewKeyword] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [testStatus, setTestStatus] = useState<{ loading: boolean; result: string | null; success?: boolean; rlsIssue?: boolean }>({ loading: false, result: null });

  const handleSave = () => {
    setIsSaving(true);
    onSave(localConfig);
    setTimeout(() => {
      setIsSaving(false);
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 3000);
    }, 800);
  };

  const handleTestConnection = async () => {
    setTestStatus({ loading: true, result: "TESTING..." });
    const res = await supabaseService.testConnection(localConfig);
    setTestStatus({ loading: false, result: res.message.toUpperCase(), success: res.success, rlsIssue: res.rlsIssue });
  };

  const addKeyword = () => {
    if (!newKeyword.trim()) return;
    setLocalConfig({ ...localConfig, discoveryKeywords: [...localConfig.discoveryKeywords, newKeyword.trim()] });
    setNewKeyword('');
  };

  const inputStyle = `w-full bg-charcoal border border-white/10 rounded-sm px-5 py-4 text-sm text-white focus:outline-none focus:border-accent transition-all font-mono placeholder:text-white/5`;

  return (
    <div className="max-w-4xl space-y-20 pb-40 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* SUPABASE SECTION */}
      <section className="space-y-8">
        <div>
          <div className="text-xs font-bold text-accent uppercase tracking-widest mb-4">Core Integration</div>
          <h2 className="text-4xl font-black tracking-tight text-white mb-2">Platform Stack</h2>
          <p className="text-sm text-muted font-medium max-w-lg leading-relaxed">Connect EventPulse to your private Supabase infrastructure to persist and synchronize event intelligence.</p>
        </div>
        
        <div className="space-y-10 p-10 bg-surface/50 border border-white/5 rounded-sm shadow-inner">
           <div className="space-y-3">
              <label className="text-[10px] font-bold text-muted uppercase tracking-[0.2em] ml-1">Project Endpoint</label>
              <input 
                type="text" 
                placeholder="https://project-id.supabase.co"
                value={localConfig.supabaseUrl || ''}
                onChange={(e) => setLocalConfig({...localConfig, supabaseUrl: e.target.value.trim()})}
                className={inputStyle}
              />
          </div>
          
          <div className="space-y-3">
              <label className="text-[10px] font-bold text-muted uppercase tracking-[0.2em] ml-1">Private Anon Key</label>
              <input 
                type="password" 
                placeholder="eyJhbG..."
                value={localConfig.supabaseAnonKey || ''}
                onChange={(e) => setLocalConfig({...localConfig, supabaseAnonKey: e.target.value.trim()})}
                className={inputStyle}
              />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
                <label className="text-[10px] font-bold text-muted uppercase tracking-[0.2em] ml-1">Events Table</label>
                <input 
                  type="text" 
                  placeholder="AI_INTELLIGENCE_RECORDS"
                  value={localConfig.supabaseTableName || ''}
                  onChange={(e) => setLocalConfig({...localConfig, supabaseTableName: e.target.value})}
                  className={inputStyle}
                />
            </div>
            <div className="space-y-3">
                <label className="text-[10px] font-bold text-muted uppercase tracking-[0.2em] ml-1">Comments Table</label>
                <input 
                  type="text" 
                  placeholder="event_comments"
                  value={localConfig.supabaseCommentsTableName || 'event_comments'}
                  onChange={(e) => setLocalConfig({...localConfig, supabaseCommentsTableName: e.target.value})}
                  className={inputStyle}
                />
            </div>
          </div>

          <div className="flex items-center gap-6 pt-4">
            <button 
              onClick={handleTestConnection}
              disabled={testStatus.loading}
              className="px-8 py-3 bg-surface border border-white/10 text-[10px] font-bold uppercase tracking-widest text-white hover:border-white transition-all disabled:opacity-30"
            >
              Verify Protocol
            </button>
            {testStatus.result && (
              <span className={`text-[10px] font-black tracking-widest ${testStatus.success ? 'text-accent' : 'text-red-500'}`}>
                {testStatus.result}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* SCAN PARAMETERS */}
      <section className="space-y-8">
        <div>
          <div className="text-xs font-bold text-accent uppercase tracking-widest mb-4">Discovery Engine</div>
          <h2 className="text-4xl font-black tracking-tight text-white mb-2">Scan Vectors</h2>
          <p className="text-sm text-muted font-medium max-w-lg leading-relaxed">Define the intelligence areas the discovery engine should prioritize during automated scans.</p>
        </div>

        <div className="p-10 bg-surface/50 border border-white/5 rounded-sm space-y-10">
          <div className="flex flex-wrap gap-3">
            {localConfig.discoveryKeywords.map((k, i) => (
              <span key={i} className="px-4 py-3 bg-charcoal border border-white/10 rounded-sm text-[10px] font-bold uppercase tracking-widest text-white/80 flex items-center gap-4 group">
                {k}
                <button 
                  onClick={() => setLocalConfig({...localConfig, discoveryKeywords: localConfig.discoveryKeywords.filter((_, idx) => idx !== i)})}
                  className="text-white/20 group-hover:text-accent transition-colors text-lg font-normal"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-6">
            <input 
              type="text" 
              placeholder="Inject scan vector (e.g. LLM Security)..." 
              value={newKeyword} 
              onChange={(e) => setNewKeyword(e.target.value)} 
              onKeyDown={(e) => e.key === 'Enter' && addKeyword()}
              className={`${inputStyle} flex-1`}
            />
            <button onClick={addKeyword} className="btn-secondary text-[10px]">Add Node</button>
          </div>
        </div>
      </section>

      <div className="fixed bottom-10 left-1/2 -translate-x-1/2 w-full max-w-4xl px-8 z-50">
        <div className="p-6 bg-surface border border-white/10 rounded-sm shadow-2xl flex justify-between items-center backdrop-blur-xl">
           <button 
             onClick={() => setLocalConfig(config)} 
             className="text-[10px] font-bold text-muted hover:text-white uppercase tracking-widest transition-colors px-6"
           >
             Rollback
           </button>
           <button 
             onClick={handleSave} 
             disabled={isSaving}
             className="btn-primary text-xs px-16 py-4"
           >
             {isSaving ? 'Processing...' : isSaved ? 'System Updated' : 'Synchronize Stack'}
           </button>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;